import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const DEFAULT_HAND_PROFILE_PATH =
  "https://cdn.jsdelivr.net/npm/@webxr-input-profiles/assets@1.0/dist/profiles/generic-hand/";

class XRHandMeshModel {
  constructor(handModel, controller, path, handedness, loader = null, onLoad = null) {
    this.controller = controller;
    this.handModel = handModel;
    this.bones = [];
    this.mesh = null;
    this.skinnedMesh = null;

    if (!loader) {
      loader = new GLTFLoader();
      loader.setPath(path || DEFAULT_HAND_PROFILE_PATH);
    }

    loader.load(`${handedness}.glb`, gltf => {
      const object = gltf.scene.children[0];
      this.handModel.add(object);

      this.skinnedMesh = object.getObjectByProperty("type", "SkinnedMesh");
      if (!this.skinnedMesh) {
        console.error(`No SkinnedMesh found in ${handedness} hand model`);
        return;
      }

      this.mesh = object;
      this.skinnedMesh.frustumCulled = false;
      this.skinnedMesh.castShadow = true;
      this.skinnedMesh.receiveShadow = true;
      this.mesh.visible = false;

      // Get bones from skeleton instead of by name
      this.bones = this.skinnedMesh.skeleton.bones;
      this.mapJointsToBones();

      if (onLoad) onLoad(object);
    });
  }

  mapJointsToBones() {
    const joints = [
      "wrist",
      "thumb-metacarpal",
      "thumb-phalanx-proximal",
      "thumb-phalanx-distal",
      "thumb-tip",
      "index-finger-metacarpal",
      "index-finger-phalanx-proximal",
      "index-finger-phalanx-intermediate",
      "index-finger-phalanx-distal",
      "index-finger-tip",
      "middle-finger-metacarpal",
      "middle-finger-phalanx-proximal",
      "middle-finger-phalanx-intermediate",
      "middle-finger-phalanx-distal",
      "middle-finger-tip",
      "ring-finger-metacarpal",
      "ring-finger-phalanx-proximal",
      "ring-finger-phalanx-intermediate",
      "ring-finger-phalanx-distal",
      "ring-finger-tip",
      "pinky-finger-metacarpal",
      "pinky-finger-phalanx-proximal",
      "pinky-finger-phalanx-intermediate",
      "pinky-finger-phalanx-distal",
      "pinky-finger-tip"
    ];

    // Clear previous bone mapping
    this.boneMap = new Map();

    joints.forEach(jointName => {
      const bone = this.bones.find(b => b.name === jointName);
      if (bone) {
        bone.jointName = jointName;
        this.boneMap.set(jointName, bone);
      } else {
        console.warn(`Couldn't find ${jointName} in ${this.handedness} hand skeleton`);
      }
    });
  }

  updateMesh() {
    if (!this.controller || !this.mesh) return;

    const XRJoints = this.controller.joints;
    if (!XRJoints) return;

    let hasVisibleJoints = false;

    this.boneMap.forEach((bone, jointName) => {
      const XRJoint = XRJoints[jointName];
      if (!XRJoint || !bone) return;

      if (XRJoint.visible) {
        hasVisibleJoints = true;
        bone.position.copy(XRJoint.position);
        bone.quaternion.copy(XRJoint.quaternion);
        bone.matrixNeedsUpdate = true;
      }
    });

    // Update skeleton once per frame
    if (hasVisibleJoints) {
      this.skinnedMesh.skeleton.update();
      this.mesh.visible = true;
    } else {
      this.mesh.visible = false;
    }
  }
}

export { XRHandMeshModel };
